import { useContext, useEffect, useCallback } from 'react';

//context
import { AssessmentContext } from '../../context/AssessmentContext.js';
import { WebSocketContext } from '../../context/WebSocketContext.js';
import {
  KioskContext,
  PWA_CONFIG,
} from '../../context/KioskProviderContext.js';

//constants & helpers
import useSessionStorage from '../../hooks/useSessionStorage.js';
import * as Storage from '../common/Storage.js';
import * as TestStatus from './TestStatus.js';
import * as PwaEvents from '../../context/PwaEvents.js';
import * as WebSocketStatus from '../../context/WebSocketStatus.js';
import {
  LEARNOSITY_EVENTS,
  showCustomDialog,
  hideCustomDialog,
} from '../../scripts/learnosityScripts.js';
import {
  PROCTOR_PAUSE_DIALOG,
  PROCTOR_SAVE_DIALOG,
  PROCTOR_SUBMIT_DIALOG,
  PROCTOR_TERMINATE_DIALOG,
} from '../../components/assessment/LearnosityDialogs.js';
import {
  ABORT_LAUNCH,
  DUPLICATE_LOGIN,
  PROCTOR_ACTION,
  PROCTOR_ACTION_LAUNCH,
  PROCTOR_ACTION_ERROR,
  PROCTOR_ACTION_PAUSE,
  PROCTOR_ACTION_RESUME,
  PROCTOR_ACTION_SUBMITANDEXIT,
  PROCTOR_ACTION_SAVEANDEXIT,
  PROCTOR_ACTION_TERMINATE,
} from '../../context/WebsocketMessageType.js';

import { PSYCHOMETRIC_EVENTS } from './PyschometricEvents.js';
import { Observable } from '../../scripts/common/Observable.js';
import { PROHIBITED_ACTION } from '../../scripts/less-secure/enforceLessSecure.js';
import { setLanguageBasedOnPnp } from '../../i18n.js';

//wait is true, so notify runs synchronously.
//priority on subscribe will call the methods in priority order
//otherwise it will get invoked in the order of subscription
export const PWAEventsObservable = new Observable('pwaEvents', true);

//moved this common code here, there are scenarios where PsychometricEventHandler is not loaded yet(example: sending authReceived)
export const constructMessage = (
  event,
  eventDetails,
  learnosityRequestObj,
  loginData,
) => {
  return {
    clientTimestamp: new Date().toISOString(),
    accessCode: loginData?.accessCode,
    studentTestRefId: learnosityRequestObj?.request?.session_id,
    studentRefId: learnosityRequestObj?.request?.user_id,
    formRefId: learnosityRequestObj?.request?.activity_template_id,
    event: event,
    ...(eventDetails ? { eventDetails: eventDetails } : {}),
  };
};

const WebsocketMessageHandler = (props) => {
  const {
    testStatus,
    learnosityItemApp,
    applySaveStateChanges,
    loginData,
    updatePausedBy,
    updateTestStatus,
    updateLearnosityRequestObject,
    updatePnp,
    resetPausedResumeRelatedStorage,
    setLearnosityItemApp,
    setOpenErrorDialog,
    setSaveErrorCount,
    setOpenProctorPauseDialog,
    setOpenContinueDialog,
    setOpenSubmitDialog,
    setProctorPauseDialogProps,
    setOpenExitingDialog,
    setExitingDialogDetails,
  } = useContext(AssessmentContext);

  const { clearStorage, setStorageItem, getStorageItem, removeStorageItem } =
    useSessionStorage();
  const { socketStatus, lastJsonMessage, sendMsgToWebsocket } =
    useContext(WebSocketContext);
  const { isSecure } = useContext(KioskContext);
  const emitEvent = useCallback((event) => {
    PWAEventsObservable.notify({ ...event });
  }, []);

  useEffect(() => {
    switch (socketStatus) {
      case WebSocketStatus.OPEN:
        if (testStatus === TestStatus.TEST_AUTHENTICATED) {
          updateTestStatus(TestStatus.TEST_AWAITING_AUTHORIZATION);
        }
        break;
      default:
        break;
    }
  }, [socketStatus, testStatus, updateTestStatus]);

  const proctorEndedAction = useCallback(
    (event, dialog, learnosityItemApp) => {
      setStorageItem(Storage.STORAGE_PAUSED_BY, PwaEvents.ACTOR_PROCTOR);
      if (learnosityItemApp) {
        emitEvent({
          eventName: event,
          appInstance: learnosityItemApp,
        });
      } else {
        //because no TestEventHandler component to handle sending, emitting event will not go anywhere
        let requestObj = getStorageItem(Storage.STORAGE_SIGNED_OBJECT);
        sendMsgToWebsocket(
          constructMessage(
            PSYCHOMETRIC_EVENTS.TEST_ENDED,
            {
              user: PwaEvents.ACTOR_PROCTOR,
              action: 'exit',
            },
            requestObj,
            loginData,
          ),
          false,
        );
        sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_PROCTOR_SAVE);
        sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_FINISHED_SAVE);
        removeStorageItem(Storage.STORAGE_AUTH_TOKEN);
      }
      //set cause of pause(dialog) because of proctor ended operations - saveAndExit, submit, terminate, so we can stop sending pause event message
      setStorageItem(
        Storage.STORAGE_PAUSED_REASON,
        PwaEvents.ACTOR_PROCTOR_ENDED,
      );
      setExitingDialogDetails(dialog);
      setOpenExitingDialog(true);
    },
    [
      setStorageItem,
      emitEvent,
      setOpenExitingDialog,
      setExitingDialogDetails,
      sendMsgToWebsocket,
      getStorageItem,
      removeStorageItem,
      loginData,
    ],
  );

  const saveAndExitSuccess = useCallback(
    (learnosityItemApp) => {
      proctorEndedAction(
        PwaEvents.PROCTOR_SAVEANDEXIT,
        PROCTOR_SAVE_DIALOG(),
        learnosityItemApp,
      );
    },
    [proctorEndedAction],
  );
  const proctorSaveAndExit = useCallback(() => {
    setStorageItem(Storage.STORAGE_ENDED_ACTION, PwaEvents.PROCTOR_SAVEANDEXIT);
    let testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
    let statusBeforePause = getStorageItem(Storage.STORAGE_STATUS_BEFORE_PAUSE);
    if (
      testStatus === TestStatus.TEST_LAUNCHED ||
      testStatus === TestStatus.TEST_AWAITING_LAUNCH ||
      (testStatus === TestStatus.TEST_PAUSED &&
        (statusBeforePause === TestStatus.TEST_LAUNCHED ||
          statusBeforePause === TestStatus.TEST_AWAITING_LAUNCH))
    ) {
      saveAndExitSuccess(null);
      return;
    }
    //emit a custom PWA events, calling save will not trigger TEST_FINISHED_SAVE automatically but we need to exit for this case
    learnosityItemApp?.save({
      success: () => {
        saveAndExitSuccess(learnosityItemApp);
      },
      // Allows you to immediately handle a save error event
      error: (event) => {
        learnosityItemApp.once(LEARNOSITY_EVENTS.TEST_SAVE_SUCCESS, () => {
          setOpenErrorDialog(false);
          saveAndExitSuccess(learnosityItemApp);
        });
        //note: need to set both testStatus & saveErrorCount, so the error dialog shows up immediately(invoked from AssessmentOverlay)
        applySaveStateChanges(TestStatus.SAVE_ERROR);
        setSaveErrorCount(TestStatus.MIN_CONSECUTIVE_ERRORS);
      },
    });
  }, [
    learnosityItemApp,
    setSaveErrorCount,
    setOpenErrorDialog,
    setStorageItem,
    saveAndExitSuccess,
    applySaveStateChanges,
    getStorageItem,
  ]);

  const submitAndExitSuccess = useCallback(
    (learnosityItemApp) => {
      proctorEndedAction(
        PwaEvents.PROCTOR_SUBMITANDEXIT,
        PROCTOR_SUBMIT_DIALOG(),
        learnosityItemApp,
      );
    },
    [proctorEndedAction],
  );

  const proctorSubmitAndExit = useCallback(() => {
    setStorageItem(
      Storage.STORAGE_ENDED_ACTION,
      PwaEvents.PROCTOR_SUBMITANDEXIT,
    );
    learnosityItemApp?.submit({
      success: () => {
        submitAndExitSuccess(learnosityItemApp);
      },
      // Allows you to immediately handle a save error event
      error: (event) => {
        learnosityItemApp.once(LEARNOSITY_EVENTS.TEST_SAVE_SUCCESS, () => {
          setOpenErrorDialog(false);
          proctorSubmitAndExit();
        });
        //note: need to set both testStatus & saveErrorCount, so the error dialog shows up immediately(invoked from AssessmentOverlay)
        applySaveStateChanges(TestStatus.SAVE_ERROR);
        setSaveErrorCount(TestStatus.MIN_CONSECUTIVE_ERRORS);
      },
    });
  }, [
    learnosityItemApp,
    setSaveErrorCount,
    setOpenErrorDialog,
    setStorageItem,
    submitAndExitSuccess,
    applySaveStateChanges,
  ]);

  const proctorTerminate = useCallback(() => {
    setStorageItem(Storage.STORAGE_ENDED_ACTION, PwaEvents.PROCTOR_TERMINATE);
    proctorEndedAction(
      PwaEvents.PROCTOR_TERMINATE,
      PROCTOR_TERMINATE_DIALOG(),
      learnosityItemApp,
    );
  }, [proctorEndedAction, setStorageItem, learnosityItemApp]);

  const closePauseDialogs = useCallback(() => {
    setOpenProctorPauseDialog(false);
    setOpenContinueDialog(false);
    setOpenSubmitDialog(false);
  }, [setOpenProctorPauseDialog, setOpenContinueDialog, setOpenSubmitDialog]);

  useEffect(() => {
    let testStatus;
    if (lastJsonMessage) {
      switch (lastJsonMessage?.messageType) {
        case DUPLICATE_LOGIN:
          updateTestStatus(TestStatus.TEST_DUPLICATE);
          setLearnosityItemApp(undefined);
          setOpenErrorDialog(true);
          break;
        case ABORT_LAUNCH:
          updateTestStatus(TestStatus.TEST_ABORT);
          setLearnosityItemApp(undefined);
          setOpenErrorDialog(true);
          break;
        case PROCTOR_ACTION:
          switch (lastJsonMessage?.messageData?.action) {
            case PROCTOR_ACTION_LAUNCH:
              let storedSignedObj = getStorageItem(Storage.STORAGE_SIGNED_OBJECT);
              if (!learnosityItemApp && !storedSignedObj) {
                let pnp = lastJsonMessage.messageData.request_obj?.pnp;
                //set the language here
                setLanguageBasedOnPnp(pnp);
                let requestObj =
                  lastJsonMessage.messageData.request_obj
                    ?.learnositySignedPayload;
                updateLearnosityRequestObject(requestObj);
                updatePnp(pnp);

                let formInfo =
                  lastJsonMessage.messageData.request_obj?.formInfo;
                if (formInfo) {
                  setStorageItem(Storage.STORAGE_FORM_INFO, formInfo);
                }

                if (!isSecure) {
                  updateTestStatus(TestStatus.TEST_AWAITING_LAUNCH);
                } else {
                  updateTestStatus(TestStatus.TEST_LAUNCHED);
                }
                let waitingOnUser = !isSecure && !pnp?.screenReader;
                sendMsgToWebsocket(PwaEvents.AUTH_RECEIVED, true, null, {
                  pwaConfig: isSecure
                    ? PWA_CONFIG.SECURE
                    : PWA_CONFIG.NON_SECURE,
                  waitingOnUser: waitingOnUser,
                  ...(waitingOnUser && {
                    waitingReason: PROHIBITED_ACTION.NOT_FULLSCREEN.type,
                  }),
                });
              }
              break;
            case PROCTOR_ACTION_PAUSE:
              testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
              updatePausedBy(PwaEvents.ACTOR_PROCTOR);
              setStorageItem(Storage.STORAGE_STATUS_BEFORE_PAUSE, testStatus); //store the current testStatus so we know what status to return on resume
              if (
                testStatus === TestStatus.TEST_LAUNCHED ||
                testStatus === TestStatus.TEST_AWAITING_LAUNCH
              ) {
                //needed because there's no learnosity pause event that will be emitted for pausing on testLaunched status so updateTestStatus in TestEventHandler will not run
                updateTestStatus(TestStatus.TEST_PAUSED); //instead we update here
              }

              if (testStatus === TestStatus.TEST_AWAITING_LAUNCH) {
                //No TestEventHandler component yet, so we need to call sendMessage directly
                let requestObj = getStorageItem(Storage.STORAGE_SIGNED_OBJECT);
                sendMsgToWebsocket(
                  constructMessage(
                    PSYCHOMETRIC_EVENTS.TEST_PAUSED,
                    { user: TestStatus.PAUSED_BY_PROCTOR },
                    requestObj,
                    loginData,
                  ),
                  false,
                );
              } else if (
                testStatus === TestStatus.TEST_PAUSED ||
                testStatus === TestStatus.TEST_LAUNCHED
              ) {
                // done so we can send pause event to TA, this is to help them when TA is out of sync with the current state
                // out of sync: proctor tries to pause an assessment that is already in paused state
                emitEvent({
                  eventName: PwaEvents.PROCTOR_PAUSE,
                  appInstance: learnosityItemApp,
                });
              }

              if (testStatus === TestStatus.TEST_INSESSION) {
                showCustomDialog(
                  learnosityItemApp,
                  PROCTOR_PAUSE_DIALOG(),
                  testStatus === TestStatus.TEST_PAUSED,
                );
              }
              setProctorPauseDialogProps(PROCTOR_PAUSE_DIALOG());
              setOpenProctorPauseDialog(true);
              setStorageItem(Storage.STORAGE_PROCTOR_RESUME_NEEDED, true);
              break;
            case PROCTOR_ACTION_RESUME:
              setStorageItem(
                Storage.STORAGE_RESUMED_BY,
                PwaEvents.ACTOR_PROCTOR,
              );
              testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
              let statusBeforePause = getStorageItem(
                Storage.STORAGE_STATUS_BEFORE_PAUSE,
              );
              if (testStatus !== TestStatus.TEST_PAUSED) {
                // done so we can send pause event to TA, this is to help them when TA is out of sync with the current state
                // out of sync: proctor tries to pause an assessment that is already in paused state
                emitEvent({
                  eventName: PwaEvents.PROCTOR_RESUME,
                  appInstance: learnosityItemApp,
                });
              } else if (
                statusBeforePause === TestStatus.TEST_AWAITING_LAUNCH
              ) {
                closePauseDialogs();
                let requestObj = getStorageItem(Storage.STORAGE_SIGNED_OBJECT);
                sendMsgToWebsocket(
                  constructMessage(
                    PSYCHOMETRIC_EVENTS.TEST_RESUMED,
                    { user: TestStatus.PAUSED_BY_PROCTOR },
                    requestObj,
                    loginData,
                  ),
                  false,
                );

                updateTestStatus(statusBeforePause);
                resetPausedResumeRelatedStorage();
              } else if (!isSecure) {
                //if not secure, let LessSecure handle the resume - needs to check things before resuming
                emitEvent({
                  eventName: PwaEvents.PROCTOR_RESUME_CHECK,
                  appInstance: learnosityItemApp,
                });
              } else {
                // for isSecure
                if (
                  statusBeforePause &&
                  statusBeforePause === TestStatus.TEST_LAUNCHED
                ) {
                  //this will handle resuming on launched(before test:start)
                  closePauseDialogs();
                  emitEvent({
                    eventName: PwaEvents.PROCTOR_RESUME,
                  });
                } else {
                  learnosityItemApp?.dialogs()?.pause.hide();
                  hideCustomDialog(learnosityItemApp, () => {
                    closePauseDialogs();
                  });
                }
              }
              break;
            case PROCTOR_ACTION_SAVEANDEXIT:
              proctorSaveAndExit();
              break;
            case PROCTOR_ACTION_SUBMITANDEXIT:
              proctorSubmitAndExit();
              break;
            case PROCTOR_ACTION_TERMINATE:
              proctorTerminate();
              break;
            case PROCTOR_ACTION_ERROR:
              updateTestStatus(TestStatus.TEST_DENIED);
              clearStorage();
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    }
  }, [
    lastJsonMessage,
    updateLearnosityRequestObject,
    updatePnp,
    clearStorage,
    setStorageItem,
    updateTestStatus,
    setLearnosityItemApp,
    setOpenErrorDialog,
    learnosityItemApp,
    updatePausedBy,
    proctorSaveAndExit,
    proctorSubmitAndExit,
    proctorTerminate,
    setOpenProctorPauseDialog,
    closePauseDialogs,
    setOpenSubmitDialog,
    setProctorPauseDialogProps,
    emitEvent,
    getStorageItem,
    isSecure,
    sendMsgToWebsocket,
    loginData,
    resetPausedResumeRelatedStorage,
  ]);

  return;
};
export default WebsocketMessageHandler;
